import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SPORT_DEFAULT_RADII, getSportLabel } from "@/lib/sport-defaults";

interface SportPickerProps {
  value: string | null;
  onChange: (sportType: string) => void;
  locationSports: string[];
  disabled?: boolean;
  placeholder?: string;
}

export function SportPicker({
  value,
  onChange,
  locationSports,
  disabled,
  placeholder = "Select sport…",
}: SportPickerProps) {
  const [open, setOpen] = useState(false);

  const { locationGroup, catalogGroup } = useMemo(() => {
    const locSet = new Set(locationSports);
    const locGroup = Array.from(locSet).sort();
    const catalog = Object.keys(SPORT_DEFAULT_RADII).sort();
    return { locationGroup: locGroup, catalogGroup: catalog };
  }, [locationSports]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
          )}
        >
          {value ? getSportLabel(value) : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search sports…" />
          <CommandList>
            <CommandEmpty>No sports found.</CommandEmpty>
            {locationGroup.length > 0 && (
              <>
                <CommandGroup heading="At this location">
                  {locationGroup.map((sport) => (
                    <CommandItem
                      key={`loc-${sport}`}
                      value={sport}
                      onSelect={() => {
                        onChange(sport);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === sport ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {getSportLabel(sport)}
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup heading="All sports">
              {catalogGroup.map((sport) => (
                <CommandItem
                  key={`cat-${sport}`}
                  value={sport}
                  onSelect={() => {
                    onChange(sport);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === sport ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {getSportLabel(sport)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
